import React, { Fragment, useMemo, useState } from "react";
import { NavLink as RouterLink } from "react-router-dom";
import clsx from "clsx";
import { Button, List, ListItem, Collapse, makeStyles, Tooltip } from "@material-ui/core";
import ExpandLess from "@material-ui/icons/ExpandLess";
import ExpandMore from "@material-ui/icons/ExpandMore";
import * as constants from "../../../utils/constants";
import { useCurrentRental } from "../../../hooks/rentalHooks";
import getCountryFromURL from "src/utils/getCountryFromUrl";

const useStyles = makeStyles((theme) => ({
  item: {
    display: "flex",
    paddingTop: 0,
    paddingBottom: 0,
  },
  button: {
    color: "black",
    fontWeight: theme.typography.fontWeightMedium,
    justifyContent: "flex-start",
    letterSpacing: 0,
    padding: "10px 8px",
    textTransform: "none",
    width: "100%",
  },
  icon: {
    marginRight: theme.spacing(1),
  },
  title: {
    marginRight: "auto",
  },
  active: {
    color: "black",
    borderRadius: 0,
    borderBottom: `4px solid ${theme.palette.primary.main}`,
  },
  nested: {
    "& $item $button": { paddingLeft: theme.spacing(4) },
    "& $nested $item $button": { paddingLeft: theme.spacing(8) },
    "& $nested $nested $item $button": { paddingLeft: theme.spacing(12) },
    "& $nested $nested $nested $item $button": { paddingLeft: theme.spacing(16) },
  },
}));

const buildExternalHandlers = (currentRental) => {
  const country = currentRental?.country ?? getCountryFromURL() ?? "au";
  const countryLower = String(country).toLowerCase();

  const safeFocus = () => {
    if (typeof window !== "undefined" && typeof window.focus === "function") {
      window.focus();
    }
  };

  return {
    openFleetGuide: () => {
      window.open(constants.HERTZ_FLEET_URL, "_blank", "noopener");
      safeFocus();
    },
    openTerms: () => {
      const trace = currentRental?.esignature_trace;
      const lastSignDoc =
        trace?.documents?.[trace.documents.length - 1]?.url ?? "";
      const isDevEnv = /staging|portal|test|dev/.test(window.location.href);
      const url = `https://file-${isDevEnv ? "dev" : "prod"}.hertz.asiapac.io/view/${lastSignDoc}`;
      window.open(url, "_blank", "noopener");
      safeFocus();
    },
    openFAQ: () => {
      const faqUrl = constants.HERTZ_FAQ?.[countryLower];
      if (faqUrl) {
        window.open(faqUrl, "_blank", "noopener");
        safeFocus();
      }
    },
  };
};

const Item = ({
  className,
  href,
  icon: Icon,
  title,
  items,
  locked,
  disabled,
  action, // external action identifier
  children,
  ...rest
}) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const currentRental = useCurrentRental();
  const handlers = useMemo(
    () => buildExternalHandlers(currentRental),
    [currentRental]
  );

  const hasChildren = Array.isArray(items) && items.length > 0;
  const isExternal = Boolean(action);

  const onItemClick = (e) => {
    if (disabled) {
      return;
    }
    if (isExternal && handlers[action]) {
      e.preventDefault();
      handlers[action]();
      return;
    }
    if (hasChildren) {
      setOpen((o) => !o);
    }
  };

  const ButtonInner = (
    <>
      {Icon && <Icon className={classes.icon} size="20" />}
      <span className={classes.title}>{title}</span>
      {!locked && hasChildren && (open ? <ExpandLess /> : <ExpandMore />)}
    </>
  );

  const ButtonNode = isExternal ? (
    // External: plain button (no NavLink), click triggers handler
    <Button className={classes.button} disabled={disabled}>
      {ButtonInner}
    </Button>
  ) : (
    // Internal: NavLink to route; React Router v5 pattern via activeClassName
    <Button
      {...{ activeClassName: classes.active }}
      className={classes.button}
      component={RouterLink}
      to={href}
      disabled={disabled}
    >
      {ButtonInner}
    </Button>
  );

  // Tooltip still shows for disabled buttons (wrap with span)
  const WithTooltip = (
    <Tooltip
      title={disabled ? "Not available for this rental" : ""}
      placement="right"
    >
      <span>{ButtonNode}</span>
    </Tooltip>
  );

  return (
    <Fragment>
      <ListItem
        button
        onClick={onItemClick}
        className={clsx(classes.item, className)}
        disableGutters
        {...rest}
      >
        {WithTooltip}
      </ListItem>

      {hasChildren && (
        <Collapse
          in={open || locked}
          timeout="auto"
          unmountOnExit
          className={classes.nested}
        >
          {children}
        </Collapse>
      )}
    </Fragment>
  );
};

const NavItem = ({
  href,
  icon: Icon,
  title,
  items,
  locked,
  disabled,
  action,
}) => {
  return (
    <Item
      {...{ href, title, icon: Icon, locked, items, disabled, action }}
    >
      <List component="div" disablePadding>
        {items &&
          items.map((item, i) => (
            <NavItem key={item.id || `${title}-${i}`} {...item} />
          ))}
      </List>
    </Item>
  );
};

export default NavItem;